// Renders an in-app notification row from its `(type, payload)` pair.
//
// Each branch handles only the fields it actually relies on. Unknown types
// fall back to a title built from the type name (#1591) so a newly-added
// type where the bell ships ahead of UI copy still reads as something, not
// the literal "New notification". The catalog here mirrors
// prisma/schema.prisma's NotificationType enum.

export interface FormattedNotification {
  title: string
  description: string | null
  href: string | null
}

type Payload = Record<string, unknown>

function pickString(p: Payload, key: string): string | null {
  const v = p[key]
  return typeof v === 'string' ? v : null
}

function bookingHref(p: Payload): string | null {
  const id = pickString(p, 'bookingId')
  return id ? `/bookings/${id}` : null
}

function eventHref(p: Payload): string | null {
  // Event-shaped payloads don't include slugs consistently — link by id when
  // present so the user lands on the right event regardless.
  const slug = pickString(p, 'eventSlug') || pickString(p, 'slug')
  if (slug) return `/events/${slug}`
  const id = pickString(p, 'eventId')
  return id ? `/events/${id}` : null
}

/**
 * #1510 — `dashboardUrl` is a per-recipient destination some dispatches
 * already compute server-side (refund-dispute-notifications.helper.ts):
 * a host gets `/hosting/refunds` (no per-request page exists for them,
 * #1162), an admin gets `/admin/refunds/:id`. `bookingHref` always points
 * at `/bookings/:id`, which the API 403s for anyone but the booking's own
 * buyer — so an admin (or a host) clicking a refund notification landed on
 * a failed page even though the server had already resolved the right
 * destination and sent it along. Absolute (built from the API's own base
 * URL); reduced to a path so Next's `Link` still routes client-side.
 */
function dashboardHref(p: Payload): string | null {
  return pathOf(pickString(p, 'dashboardUrl'))
}

/** Reduce an absolute URL the API built to a path so Next's `Link` routes client-side. */
function pathOf(url: string | null): string | null {
  if (!url) return null
  try {
    const parsed = new URL(url)
    return `${parsed.pathname}${parsed.search}`
  } catch {
    return url
  }
}

/**
 * #1591: readable fallback for a type with no template yet:
 * `FACILITATOR_PAYOUT_SUMMARY` becomes "Facilitator payout summary". An
 * admin-fanout suffix is dropped because the recipient already is the admin.
 */
export function humanizeNotificationType(type: string): string {
  const words = type
    .replace(/_ADMIN$/, '')
    .toLowerCase()
    .split('_')
    .filter(Boolean)
  if (words.length === 0) return 'New notification'
  const [first, ...rest] = words
  return [first.charAt(0).toUpperCase() + first.slice(1), ...rest].join(' ')
}

export function formatNotification(
  type: string,
  payload: Payload | null | undefined,
): FormattedNotification {
  const p: Payload = payload ?? {}
  const eventTitle = pickString(p, 'eventTitle')

  switch (type) {
    case 'WELCOME':
      return {
        title: 'Welcome to Path Collective',
        description: 'Your account is ready. Explore events to get started.',
        href: '/discovery',
      }
    case 'ACCOUNT_DELETED':
      return {
        title: 'Account deleted',
        description: 'Your account has been removed.',
        href: null,
      }
    case 'FACILITATOR_APPLICATION_SUBMITTED':
      return {
        title: 'Facilitator application received',
        description: 'We will review and email you the outcome.',
        href: '/facilitator/application/pending',
      }
    case 'APPLICATION_OUTCOME':
      return {
        title: 'Facilitator application update',
        description: pickString(p, 'message') ?? 'Your application status has changed.',
        href: '/facilitator/application/pending',
      }
    case 'EVENT_CREATED':
      return {
        title: eventTitle
          ? `New event: ${eventTitle}`
          : 'New event from a facilitator you follow',
        description: pickString(p, 'facilitatorName')
          ? `Hosted by ${pickString(p, 'facilitatorName')}`
          : null,
        href: eventHref(p),
      }
    case 'EVENT_MODIFIED':
      return {
        title: eventTitle ? `Event updated: ${eventTitle}` : 'Event updated',
        description: pickString(p, 'changeSummary') ?? 'A booked event has changed.',
        href: bookingHref(p) ?? eventHref(p),
      }
    case 'EVENT_CANCELLED':
      return {
        title: eventTitle ? `Event cancelled: ${eventTitle}` : 'Event cancelled',
        description: 'A booked event was cancelled by the host.',
        href: bookingHref(p),
      }
    case 'BOOKING_CONFIRMED_MEMBER':
      return {
        title: 'Booking confirmed',
        description: eventTitle ? `You are booked for ${eventTitle}.` : 'Your booking is confirmed.',
        href: bookingHref(p),
      }
    case 'BOOKING_CONFIRMED_FACILITATOR':
      return {
        title: 'New booking on your event',
        description: eventTitle
          ? `${pickString(p, 'seekerName') ?? 'A guest'} booked ${eventTitle}.`
          : 'You have a new booking.',
        // #749 — /bookings/:id is owner-scoped and 403s the host; send the
        // facilitator to their event instead (payload carries eventId).
        href: eventHref(p) ?? null,
      }
    case 'BOOKING_MODIFIED':
      return {
        title: 'Booking updated',
        description: eventTitle ? `Details changed for ${eventTitle}.` : 'A booking was updated.',
        href: bookingHref(p),
      }
    case 'BOOKING_CANCELLED_BY_MEMBER':
      return {
        title: 'Member cancelled their booking',
        description: eventTitle
          ? `${pickString(p, 'seekerName') ?? 'A guest'} cancelled for ${eventTitle}.`
          : 'A booking was cancelled.',
        href: eventHref(p) ?? null,
      }
    case 'REFUND_REQUESTED':
      if (p.escalated) {
        // #119 — RefundSlaScheduler fires this admin-only fanout when a
        // request crosses its 5-day SLA without a practitioner decision.
        return {
          title: 'Refund SLA escalated',
          description:
            'A refund request hit the 5-day SLA and was auto-escalated to admin.',
          // #1510 — admin-only, so always /admin/refunds/:id.
          href: dashboardHref(p) ?? bookingHref(p),
        }
      }
      return {
        title: 'Refund requested',
        description: 'A member is requesting a refund on one of your events.',
        // #1510 — the host gets /hosting/refunds, admin gets
        // /admin/refunds/:id; bookingHref 403s for both (the booking's
        // buyer, not the host or admin, "owns" /bookings/:id).
        href: dashboardHref(p) ?? bookingHref(p),
      }
    case 'REFUND_DECISION':
      return {
        title: 'Refund decision',
        description: pickString(p, 'outcome') === 'DENIED'
          ? 'Your refund request was denied.'
          : 'Your refund was approved.',
        href: bookingHref(p),
      }
    case 'REFUND_AUTO_APPROVED':
      return {
        title: 'Refund auto-approved',
        description: 'A refund was auto-approved by SLA timeout.',
        // #1521 — the host/admin fanout now carries dashboardUrl (same
        // convention as REFUND_REQUESTED above); the buyer's own copy has
        // none, so this still falls back to bookingHref(p) for them.
        href: dashboardHref(p) ?? bookingHref(p),
      }
    case 'DISPUTE_OPENED':
      return {
        title: 'Stripe dispute opened',
        description: 'A buyer disputed a charge on one of your events.',
        // #1521 — dispatchDisputeOpened already computes dashboardUrl
        // per-recipient; the formatter just never read it.
        href: dashboardHref(p) ?? bookingHref(p),
      }
    case 'DISPUTE_RESOLVED':
      return {
        title: 'Stripe dispute resolved',
        description: 'An open dispute has closed.',
        href: dashboardHref(p) ?? bookingHref(p),
      }
    case 'BOOKING_REMINDER_24H':
      return {
        title: 'Tomorrow: ' + (eventTitle ?? 'your booked event'),
        description: 'Reminder one day before your event.',
        href: bookingHref(p),
      }
    case 'BOOKING_REMINDER_1H':
      return {
        title: 'Starting soon: ' + (eventTitle ?? 'your booked event'),
        description: 'Final reminder one hour before your event.',
        href: bookingHref(p),
      }
    case 'BOOKING_REMINDER_2H_ONLINE':
      return {
        title: 'Starting soon: ' + (eventTitle ?? 'your online event'),
        description: 'Your join link, two hours before an online event.',
        href: bookingHref(p),
      }
    case 'FACILITATOR_REMINDER_24H':
      return {
        title: 'Hosting tomorrow: ' + (eventTitle ?? 'your event'),
        description: 'One day before an event you are hosting.',
        href: bookingHref(p) ?? eventHref(p),
      }
    case 'MATCHING_DIGEST_WEEKLY':
      return {
        title: 'Weekly recommendations',
        description: 'Events that match your interests this week.',
        href: '/discovery',
      }
    case 'ACTIVITY_DIGEST_WEEKLY':
      return {
        title: 'Weekly hosting summary',
        description: 'Bookings and refunds for the past week.',
        href: '/hosting',
      }
    case 'INACTIVITY_NUDGE':
      return {
        title: 'Find your next event',
        description: 'A few picks based on your interests.',
        href: '/discovery',
      }
    case 'ABANDONED_BOOKING_REMINDER':
      return {
        title: 'Finish your booking',
        description: eventTitle ? `You left ${eventTitle} in checkout.` : 'You have an unfinished checkout.',
        href: bookingHref(p) ?? eventHref(p),
      }
    case 'SAVED_EVENT_REMINDER':
      return {
        title: 'Your saved event is coming up',
        description: eventTitle ? `${eventTitle} starts in 48 hours.` : 'A saved event is starting soon.',
        href: eventHref(p),
      }
    case 'FACILITATOR_PAYOUT':
      return {
        title: 'Payout sent',
        description: 'Funds for one of your events have been transferred to your bank.',
        href: '/hosting',
      }
    case 'TAX_INVOICE_ISSUED': {
      // #558 — the document itself is in the email; the feed row links to
      // the printable page.
      const number = pickString(p, 'documentNumber')
      const id = pickString(p, 'bookingId')
      return {
        title: number ? `Tax invoice ${number} issued` : 'Tax invoice issued',
        description: eventTitle ? `For ${eventTitle}. Tap to view or print.` : 'Tap to view or print.',
        href: id ? `/bookings/${id}/receipt` : bookingHref(p),
      }
    }
    case 'ORGANIZER_ORDER_COPY':
      return {
        title: 'Order confirmation',
        description: eventTitle ? `New order on ${eventTitle}.` : 'New order on one of your events.',
        href: bookingHref(p),
      }
    case 'CHECKED_IN_CONFIRMATION':
      return {
        title: eventTitle ? `Checked in to ${eventTitle}` : "You're checked in",
        description: 'Enjoy the event. You can leave a review once it ends.',
        href: bookingHref(p) ?? eventHref(p),
      }
    case 'CUSTOMER_SERVICE_TICKET': {
      // A-5 — admin replied to a seeker's ticket.
      const subject = pickString(p, 'subject')
      const ticketId = pickString(p, 'ticketId')
      return {
        title: subject ? `Reply on "${subject}"` : 'Support reply',
        description: 'Tap to read and reply.',
        href: ticketId ? `/support/${ticketId}` : '/support',
      }
    }
    case 'ASSESSMENT_FAILED_ADMIN': {
      // #548 — admin-fanout alert when an AI assessment lands FAILED; the
      // applicant is stuck PENDING with no review rows until it is re-run.
      const applicantUserId = pickString(p, 'applicantUserId')
      return {
        title: 'AI assessment failed',
        description:
          'A facilitator assessment failed — the applicant is stuck until it is re-run.',
        href: applicantUserId
          ? `/admin/facilitator/${applicantUserId}`
          : '/admin',
      }
    }
    case 'CUSTOMER_SERVICE_TICKET_OPENED': {
      // A-8 — admin-fanout alert when a seeker opens a new ticket.
      const subject = pickString(p, 'subject')
      const ticketId = pickString(p, 'ticketId')
      const kind = pickString(p, 'kind')
      return {
        title: kind ? `New support ticket (${kind})` : 'New support ticket',
        description: subject ?? 'A member opened a ticket.',
        href: ticketId ? `/admin/support/${ticketId}` : '/admin/support',
      }
    }
    case 'NEW_CONTACTS_FROM_EVENT': {
      // #1006 — the point of the row is the number, so it goes in the title.
      // "New" means people who had not consented to this host before. Ed,
      // 2026-09-16: the description reads as the win it is rather than as a
      // consent disclosure, but it must still say "first time" — a host who
      // reads it as "attendees" would think the event under-performed.
      const count = typeof p.newContactsCount === 'number' ? p.newContactsCount : null
      const noun = count === 1 ? '1 new contact' : `${count ?? 'New'} new contacts`
      const eventId = pickString(p, 'eventId')
      const people = count === 1 ? '1 person' : `${count} people`
      const firstFor = count === 1 ? 'a first for them' : 'a first for each of them'
      return {
        title: count === null ? 'New contacts from your event' : `You have ${noun}`,
        description:
          count === null
            ? 'People who said yes to hearing from you for the first time.'
            : eventTitle
              ? `${people} at ${eventTitle} said yes to hearing from you — ${firstFor}.`
              : `${people} said yes to hearing from you — ${firstFor}.`,
        href: eventId ? `/hosting/crm?eventId=${eventId}` : '/hosting/crm',
      }
    }
    case 'EVENT_COHOST_INVITED': {
      // #1005 — the recipient is NOT on the event yet; the copy must not imply
      // they are. The row links to the page where they accept or decline.
      const inviterName = pickString(p, 'inviterName')
      return {
        title: eventTitle
          ? `Invitation to co-host: ${eventTitle}`
          : 'Invitation to co-host an event',
        description: inviterName
          ? `${inviterName} invited you. You are listed only once you accept.`
          : 'You are listed only once you accept.',
        href: '/hosting/invitations',
      }
    }
    case 'EVENT_COHOST_ACCEPTED': {
      const cohostName = pickString(p, 'cohostName')
      return {
        title: cohostName
          ? `${cohostName} accepted your co-host invitation`
          : 'Co-host invitation accepted',
        description: eventTitle,
        href: eventHref(p),
      }
    }
    case 'EVENT_COHOST_DECLINED': {
      const cohostName = pickString(p, 'cohostName')
      return {
        title: cohostName
          ? `${cohostName} declined your co-host invitation`
          : 'Co-host invitation declined',
        description: eventTitle,
        href: eventHref(p),
      }
    }
    case 'FACILITATOR_APPLICATION_SUBMITTED_ADMIN': {
      // #1591: admin fanout from facilitator-signup.service.ts; the payload
      // carries only the applicant's user id (the name is resolved at review).
      const applicantUserId = pickString(p, 'applicantUserId')
      return {
        title: 'New facilitator application',
        description: 'A facilitator application is ready for review.',
        href: applicantUserId ? `/admin/facilitator/${applicantUserId}` : '/admin',
      }
    }
    case 'AMBASSADOR_HEALTH_REVIEW_ADMIN': {
      // #1591: weekly admin fanout from ambassador-review.scheduler.ts. The
      // surface is a review page, not an action; the payload already carries
      // both the ambassadors list and the per-facilitator admin page.
      const name = pickString(p, 'facilitatorName')
      const facilitatorUserId = pickString(p, 'facilitatorUserId')
      const windowDays = typeof p.windowDays === 'number' ? p.windowDays : null
      return {
        title: name ? `Ambassador review: ${name}` : 'Ambassador review needed',
        description: windowDays
          ? `Below the activity thresholds over the last ${windowDays} days. Worth a conversation.`
          : 'Below the activity thresholds. Worth a conversation.',
        href:
          pathOf(pickString(p, 'facilitatorUrl')) ??
          (facilitatorUserId ? `/admin/facilitator/${facilitatorUserId}` : null) ??
          pathOf(pickString(p, 'reviewUrl')) ??
          '/admin/ambassadors',
      }
    }
    case 'GUEST_TICKET_RESEND': {
      // #1591: events.service.ts sends the guest their ticket link(s) again.
      // The guest has no account, so /bookings/:id would 403; the payload's
      // `ticketUrl` is the guest ticket page keyed by booking id and email.
      const tickets = Array.isArray(p.tickets) ? (p.tickets as Payload[]) : []
      const first = tickets[0] ?? {}
      const count = tickets.length
      return {
        title: eventTitle ? `Your tickets for ${eventTitle}` : 'Your tickets',
        description:
          count > 1
            ? `${count} tickets sent again to your email.`
            : 'Your ticket was sent again to your email.',
        href: pathOf(pickString(first, 'ticketUrl')) ?? bookingHref(first) ?? eventHref(p),
      }
    }
    default:
      return {
        title: humanizeNotificationType(type),
        description: eventTitle ?? pickString(p, 'message'),
        href: dashboardHref(p) ?? bookingHref(p) ?? eventHref(p),
      }
  }
}

export function formatRelativeTime(iso: string): string {
  const then = new Date(iso).getTime()
  if (!Number.isFinite(then)) return ''
  const diffMs = Date.now() - then
  const minute = 60 * 1000
  const hour = 60 * minute
  const day = 24 * hour

  if (diffMs < minute) return 'just now'
  if (diffMs < hour) return `${Math.floor(diffMs / minute)}m ago`
  if (diffMs < day) return `${Math.floor(diffMs / hour)}h ago`
  if (diffMs < 7 * day) return `${Math.floor(diffMs / day)}d ago`
  return new Date(iso).toLocaleDateString()
}
